/// <amd-module name="Wit/DeliveryPlans/Board/Graph/DependencyGraphOverlay" />
import * as React from "react";
import { IObservableValue } from "VSS/Core/Observable";
import { css } from "VSS/Platform/Layout";
import { WorkItemDependencyData } from "Wit/DeliveryPlans/Common/Client/Contracts/DataProvider";
import { CardDimensions, DependencyCardElement } from "Wit/DeliveryPlans/Common/Interfaces";
import { WorkItemDependencyOverlay } from "./WorkItemDependencyOverlay";

export interface IDependencyGraphOverlayProps {
    className?: string;
    gridArea: string;
    onClick?: (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => void;
    onDependencyLineClick?: (
        event: React.MouseEvent<SVGPathElement, MouseEvent>,
        dependency: WorkItemDependencyData,
        anchorElement: HTMLElement
    ) => void;
    parentOffsetLeft: number;
    parentOffsetTop: number;
    data: DependencyCardElement[] | undefined;
    cardDimensions: IObservableValue<CardDimensions>;
}

/**
 * Graph component that will overlay a set of elements and draw lines between related items
 * based on the provided source element and list of target elements
 */
export const DependencyGraphOverlay: React.FunctionComponent<IDependencyGraphOverlayProps> = ({
    className,
    gridArea,
    onClick,
    onDependencyLineClick,
    parentOffsetLeft,
    parentOffsetTop,
    data,
    cardDimensions
}) => {
    if (!data?.length) {
        return null;
    }

    return (
        <>
            <div className="delivery-plans-canvas-overlay absolute-fill sub-layer full-height full-width"></div>
            <svg className={css("delivery-plans-canvas", className)} style={{ gridArea }} width="100%" height="100%" onClick={onClick}>
                <defs>
                    <marker
                        id="invalid-arrow"
                        viewBox="0 0 5 5"
                        markerWidth="2.5"
                        markerHeight="2.5"
                        orient="auto-start-reverse"
                        refX="2.5"
                        refY="2.5"
                    >
                        <path className="invalid-dependency-fill" d="M0,0 L5,2.5 L0,5 Z" fill="currentColor" />
                    </marker>
                    <marker id="arrow" viewBox="0 0 5 5" markerWidth="2.5" markerHeight="2.5" orient="auto-start-reverse" refX="2.5" refY="2.5">
                        <path className="valid-dependency-fill" d="M0,0 L5,2.5 L0,5 Z" fill="currentColor" />
                    </marker>
                    <marker id="invalid-icon" viewBox="0 0 12 12" markerWidth="6" markerHeight="6" refX="6" refY="6">
                        <circle className="invalid-dependency-fill" fill="currentColor" cx="6" cy="6" r="6" />
                        <path
                            d="M3.64 3.64a.75.75 0 0 1 1.06 0l1.294 1.294L7.288 3.64a.75.75 0 0 1 1.06 1.06L7.056 5.994l1.292 1.292a.75.75 0 0 1-1.06 1.06l-1.295-1.29-1.291 1.291a.75.75 0 1 1-1.06-1.06l1.292-1.293L3.64 4.7a.75.75 0 0 1 0-1.06z"
                            fill="#fff"
                        />
                    </marker>
                </defs>
                <Edges
                    data={data}
                    onDependencyLineClick={onDependencyLineClick}
                    parentOffsetLeft={parentOffsetLeft}
                    parentOffsetTop={parentOffsetTop}
                    cardDimensions={cardDimensions}
                ></Edges>
            </svg>
        </>
    );
};

export interface EdgesProps {
    onDependencyLineClick?: (
        event: React.MouseEvent<SVGPathElement, MouseEvent>,
        dependency: WorkItemDependencyData,
        anchorElement: HTMLElement
    ) => void;
    parentOffsetLeft: number;
    parentOffsetTop: number;
    data: DependencyCardElement[];
    cardDimensions: IObservableValue<CardDimensions>;
}

const Edges = React.memo<EdgesProps>(
    (props: EdgesProps) => {
        return (
            <>
                {props.data.map((dependency: DependencyCardElement) => (
                    <WorkItemDependencyOverlay
                        element={dependency}
                        onDependencyLineClick={props.onDependencyLineClick}
                        parentOffsetTop={props.parentOffsetTop}
                        parentOffsetLeft={props.parentOffsetLeft}
                        key={`${props.parentOffsetLeft}-${props.parentOffsetTop}-${dependency.workItemId}`}
                        cardDimensions={props.cardDimensions}
                    ></WorkItemDependencyOverlay>
                ))}
            </>
        );
    },
    (prev: EdgesProps, next: EdgesProps) => {
        return (
            prev.parentOffsetLeft === next.parentOffsetLeft &&
            prev.parentOffsetTop === next.parentOffsetTop &&
            (prev.data?.length ?? 0) === (next.data?.length ?? 0)
        );
    }
);
